import { Moon, Sun, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/ThemeProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export const Header = () => {
  const { theme, setTheme } = useTheme();

  return (
    <header className="border-b border-border bg-card">
      <div className="container mx-auto px-4 py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              AI Photo Analyzer
            </h1>
            <p className="text-sm text-muted-foreground">
              Upload a photo to get captions, tags, colors, OCR, and SEO alt-text. No login required.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Info className="h-4 w-4" />
                  <span className="sr-only">Privacy information</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Privacy & How It Works</DialogTitle>
                  <DialogDescription className="space-y-4 text-left pt-4">
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Privacy First</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Images are processed and deleted immediately by default</li>
                        <li>No face identification or identity recognition</li>
                        <li>GPS data is stripped from EXIF by default</li>
                        <li>All processing happens securely</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-foreground mb-2">How It Works</h3>
                      <p className="text-sm">
                        This tool uses advanced AI models to analyze your images and provide:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                        <li><strong>Captions:</strong> Human-friendly descriptions</li>
                        <li><strong>Tags:</strong> Object detection with confidence scores</li>
                        <li><strong>Colors:</strong> Dominant color palette extraction</li>
                        <li><strong>Scene Analysis:</strong> Detailed scene descriptions</li>
                        <li><strong>OCR:</strong> Text extraction from images</li>
                        <li><strong>EXIF Metadata:</strong> Camera settings and info</li>
                        <li><strong>SEO Alt-text:</strong> Optimized descriptions for web</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-semibold text-foreground mb-2">Important Notes</h3>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li>Results are AI-generated estimates</li>
                        <li>For sensitive decisions, consult a professional</li>
                        <li>This tool does not identify people by name</li>
                      </ul>
                    </div>
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === "light" ? "dark" : "light")}
              className="h-9 w-9"
            >
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
